import { AudienceIntelligence, Brand, BrandIntelligence, CompetitiveIntel, CreativeSystem, MarketIntelligence, SocialAndDigital } from "@/schema/brand-intelligence";

const brand: Brand = {
  name: "Switch Mobility Limited",
  industry: ["Motor Vehicle Manufacturing", "Electric Vehicles"],
  sub_industry: ["Electric Buses", "Light Commercial Vehicles"],
  founded_year: 1985,
  founded_original_name: "Optare",
  headquarters: ["Chennai, India", "Leeds, United Kingdom"],
  tagline_primary: "Enriching lives through green mobility",
  mission:
    '"Enriching lives through green mobility – delivering zero-emission commercial vehicles and solutions that create outstanding value for stakeholders while ensuring minimal environmental impact."',
  vision: '"To become the global leader in zero-emission commercial mobility, providing sustainable transport solutions for all."',
  overview:
    "Switch Mobility, part of the Hinduja Group, is a global electric mobility company specializing in electric buses and light commercial vehicles. Formed from Ashok Leyland's engineering and Optare's design, it delivers zero-emission solutions for urban and intercity transport, with manufacturing in Leeds (UK) and Chennai (India), and over 2,000 electric vehicles deployed worldwide.",
  scale: {
    revenue: 750,
    employees_estimate: 597,
    markets_served_count: 4,
    operating_countries_count: 50,
  },
  global_presence: {
    regions: [
      {
        region: "Asia / India",
        headquarters: "Chennai, India",
        active_markets: ["India", "South Asia"],
        market_type: "Owned / Direct manufacturing and sales",
      },
      {
        region: "Europe / United Kingdom",
        headquarters: "Leeds, United Kingdom",
        active_markets: ["United Kingdom", "Continental Europe"],
        market_type: "Owned",
      },
      {
        region: "Europe / Continental Europe",
        headquarters: "Valladolid, Spain",
        active_markets: ["Spain", "Continental Europe", "South America (planned)"],
        market_type: "Planned manufacturing",
      },
      {
        region: "Europe / United Kingdom",
        headquarters: "Warwick, UK",
        active_markets: ["Global R&D support"],
        market_type: "Research & Development",
      },
    ],
    key_markets_by_revenue_share: [
      {
        region: "India",
        share_percent: 80,
        notes:
          "80% market share in the 2-3.5-ton e-LCV segment; 31.9% share in e-bus segment (213 units in first two months of 2025); Largest market with order book of 1,520 electric buses as of September 30, 2024",
      },
      {
        region: "United Kingdom",
        share_percent: 0,
        notes: "Over 150 electric buses operating across major UK cities including London and York; Sold 33 buses in 2022, 44 in 2023, and 125 in 2024",
      },
      {
        region: "Continental Europe",
        share_percent: 0,
        notes: "Emerging market; Initial focus on electric vehicle manufacturing for the European market",
      },
      {
        region: "Export Markets",
        share_percent: 0,
        notes: "Order book includes export orders; Delivered first 10 electric buses to Mauritius (August 2025)",
      },
    ],
  },
};

const creativeSystem: CreativeSystem = {
  visual_identity: {
    colors: {
      primary: [
        {
          name: "Switch Blue",
          hex: "#0066CC",
        },
        {
          name: "Switch Navy",
          hex: "#003366",
        },
        {
          name: "White",
          hex: "#FFFFFF",
        },
      ],
      secondary: [
        {
          name: "Light Blue",
          hex: "#00A3E0",
        },
        {
          name: "Gray",
          hex: "#6B7280",
        },
        {
          name: "Light Gray",
          hex: "#F3F4F6",
        },
      ],
      accent: [
        {
          name: "Green",
          hex: "#10B981",
        },
        {
          name: "Dark Charcoal",
          hex: "#1F2937",
        },
      ],
    },
    typography: [
      {
        font_family: "Sans-serif",
        weight: "Regular (400), Medium (500)",
        usage: "Body text, general content",
      },
      {
        font_family: "Sans-serif",
        weight: "Bold (700), Semi-Bold (600)",
        usage: "Headlines, section headers",
      },
      {
        font_family: "Sans-serif",
        weight: "Medium (500)",
        usage: "Navigation, UI elements",
      },
    ],
    logos: [
      {
        variant: "Primary",
        url: "https://mma.prnewswire.com/media/2580555/SWITCH_Mobility_Logo.jpg",
      },
    ],
  },
  verbal_identity: {
    voice_traits: ["Innovative", "Sustainable", "Professional", "Aspirational", "Trustworthy", "Empowering", "Technical"],
    taglines: [
      "Enriching lives through green mobility",
      "Be a global technology leader providing zero-emission commercial mobility products and solutions",
      "Shaping the future of sustainable transport",
      "Move Better",
    ],
    messaging_dos: [],
    messaging_donts: [],
    banned_words: [],
  },
  creative_assets: ["https://mma.prnewswire.com/media/2580556/Mahesh_Babu_CEO_of_SWITCH_Mobility.jpg", "https://mma.prnewswire.com/media/2580555/SWITCH_Mobility_Logo.jpg"],
  compliance: {
    brand_safety_guidelines: [],
    copyright_trademark_usage: [],
  },
  brand_architecture: {
    available_brands: ["Switch Mobility", "Switch Mobility Automotive Ltd", "Switch Mobility Ltd", "EiV Series", "IeV Series", "Switch i-ON"],
    selected_brand: "Switch Mobility",
  },
  ai_creative_memory: {
    design_memory: {
      pattern_accuracy: "94%",
      patterns_learned: 342,
      avg_generation: "1.5s",
    },
    style_drift_alerts: [
      {
        title: "EiV12 Product Launch Campaign",
        description: "Button CTA color deviates from Switch Blue #0066CC",
        severity: "high",
      },
      {
        title: "Electric Bus Landing Page",
        description: "Heading font size inconsistent with typography scale",
        severity: "medium",
      },
      {
        title: "Social Media Ad - LinkedIn",
        description: "Logo spacing does not meet minimum clearance requirements",
        severity: "low",
      },
      {
        title: "Sustainability Brochure",
        description: "Green accent #10B981 usage exceeds recommended 20% threshold",
        severity: "medium",
      },
    ],
    preference_learning: [
      { preference: "Sustainability Focus", score: 93, change: 6 },
      { preference: "Clean Tech", score: 89, change: 4 },
      { preference: "Blue/Green Palette", score: 85, change: 2 },
      { preference: "Industrial Modern", score: 80, change: 5 },
    ],
  },
};

const marketIntelligence: MarketIntelligence = {
  kpis: {
    sentiment: 7.2,
    sentiment_graph: [
      {
        metric: "Market Position Momentum",
        value: "Rising",
        delta: "+7.9 pts H1 2025",
      },
      {
        metric: "UK Market Growth",
        value: "184%",
        delta: "+184% YoY 2024",
      },
      {
        metric: "India Market Share (Peak)",
        value: "31.9%",
        delta: "+31.9% Jan-Feb 2025",
      },
      {
        metric: "Order Book Strength",
        value: "1,800+",
        delta: "Strong pipeline",
      },
      {
        metric: "Operator Satisfaction (Estimated)",
        value: "~7.5/10",
        delta: "Repeat orders positive",
      },
      {
        metric: "Product Range Expansion",
        value: "8+ models",
        delta: "+3 models 2024-25",
      },
      {
        metric: "Manufacturing Capacity Utilization",
        value: "~65%",
        delta: "India scaling",
      },
      {
        metric: "Brand Recognition (India EV)",
        value: "High",
        delta: "Top 5 player",
      },
    ],
  },
  sentiment_summary: {
    time_range: ["January 2024", "June 2025"],
    themes: {
      positive: [
        "Strong Market Position in India",
        "Operator Trust & Repeat Orders",
        "Product Innovation & Range",
        "Manufacturing Scale & Capability",
        "Government & Institutional Support",
        "Total Cost of Ownership Benefits",
        "Zero-Emission Leadership",
      ],
      neutral: ["Market Volatility", "Geographic Concentration", "Transition Period", "Price Positioning", "Technology Partnerships"],
      negative: [
        "UK Factory Closure Consultation",
        "Historical Quality Issues",
        "Market Share Decline from Peak",
        "Slower European Adoption",
        "Financial Losses in UK Operations",
        "Limited Third-Party Reviews",
        "Charging Infrastructure Dependency",
      ],
    },
  },
  distribution: [
    {
      brand: "PMI Electro Mobility",
      share: 25.8,
    },
    {
      brand: "Switch Mobility",
      share: 24,
    },
    {
      brand: "Olectra Greentech",
      share: 17.4,
    },
    {
      brand: "JBM Auto",
      share: 14.2,
    },
    {
      brand: "Others",
      share: 18.6,
    },
  ],
  sentiment_by_product: [
    {
      product: "Switch EiV12 (India Bus)",
      sentiment: 7.5,
      change_delta: 5,
    },
    {
      product: "Switch Metrocity (UK Bus)",
      sentiment: 7.8,
      change_delta: 8,
    },
    {
      product: "Switch IeV4 (LCV)",
      sentiment: 7.2,
      change_delta: 0,
    },
    {
      product: "Switch E1 (Europe Bus)",
      sentiment: 7,
      change_delta: 0,
    },
    {
      product: "Switch IeV3 (LCV)",
      sentiment: 7,
      change_delta: 0,
    },
    {
      product: "Switch MetroDecker (Double-Decker)",
      sentiment: 6.5,
      change_delta: -15,
    },
    {
      product: "Switch EiV22 (India Double-Decker)",
      sentiment: 7.3,
      change_delta: 0,
    },
    {
      product: "Switch IeV8 (LCV)",
      sentiment: 7.5,
      change_delta: 0,
    },
  ],
  top_topics: {
    praise: [
      {
        topic: "Total Cost of Ownership (TCO) Benefits",
        weight: 10,
      },
      {
        topic: "Product Range & Versatility",
        weight: 9,
      },
      {
        topic: "Customer Comfort & Design",
        weight: 8,
      },
      {
        topic: "Delivery Reliability & Scale",
        weight: 8,
      },
      {
        topic: "Technical Support & Service Network",
        weight: 7,
      },
      {
        topic: "Battery Technology & Range",
        weight: 7,
      },
      {
        topic: "Zero-Emission Credentials",
        weight: 6,
      },
    ],
    complaints: [
      {
        topic: "Historical Safety Incidents",
        weight: 10,
      },
      {
        topic: "UK Market Uncertainty",
        weight: 9,
      },
      {
        topic: "Lead Times & Delivery Schedules",
        weight: 8,
      },
      {
        topic: "Charging Infrastructure Dependency",
        weight: 8,
      },
      {
        topic: "Premium Pricing vs. Competitors",
        weight: 7,
      },
      {
        topic: "Limited European Market Presence",
        weight: 7,
      },
      {
        topic: "Range Anxiety for Certain Routes",
        weight: 6,
      },
    ],
  },
};

const competitiveIntel: CompetitiveIntel = {
  primary_competitors: [
    {
      name: "BYD Company Limited",
      threat_level: "high",
      market_share: 13.6,
      sentiment: 75,
    },
    {
      name: "Yutong Bus Co., Ltd.",
      threat_level: "high",
      market_share: 20,
      sentiment: 73,
    },
    {
      name: "Tata Motors Limited",
      threat_level: "high",
      market_share: 27.5,
      sentiment: 70,
    },
    {
      name: "Proterra Inc.",
      threat_level: "medium",
      market_share: 6.5,
      sentiment: 68,
    },
    {
      name: "AB Volvo / Volvo Buses",
      threat_level: "high",
      market_share: 16.5,
      sentiment: 72,
    },
    {
      name: "Olectra Greentech Limited",
      threat_level: "medium",
      market_share: 17.4,
      sentiment: 65,
    },
    {
      name: "JBM Auto Limited",
      threat_level: "medium",
      market_share: 13.5,
      sentiment: 67,
    },
    {
      name: "Alexander Dennis Limited",
      threat_level: "medium",
      market_share: 10,
      sentiment: 68,
    },
  ],
  secondary_competitors: [
    {
      name: "Solaris Bus & Coach",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
    {
      name: "NFI Group / New Flyer",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
    {
      name: "VDL Bus & Coach",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
    {
      name: "Daimler Truck AG / Mercedes-Benz",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
    {
      name: "King Long / Zhongtong",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
    {
      name: "IVECO Bus",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
  ],
  share_of_voice: [
    {
      channel: "Industry Media & Trade Publications",
      distribution: {
        BYD: 25,
        Yutong: 18,
        "Volvo Buses": 15,
        "Tata Motors": 12,
        "Switch Mobility": 8,
        Proterra: 7,
        Others: 15,
      },
    },
    {
      channel: "Social Media (LinkedIn/Industry Platforms)",
      distribution: {
        BYD: 22,
        Volvo: 16,
        "Tata Motors": 14,
        Yutong: 12,
        "Switch Mobility": 6,
        Others: 30,
      },
    },
    {
      channel: "Government/Tender Announcements",
      distribution: {
        BYD: 20,
        "Tata Motors": 18,
        Yutong: 15,
        "Switch Mobility": 10,
        Olectra: 8,
        Others: 29,
      },
    },
  ],
  competitive_benchmarking: [
    {
      metric_name: "Market Deployment Success Rate",
      brands: [
        {
          brand_name: "BYD",
          score: 92,
        },
        {
          brand_name: "Yutong",
          score: 88,
        },
        {
          brand_name: "Tata Motors",
          score: 85,
        },
        {
          brand_name: "Switch Mobility",
          score: 78,
        },
        {
          brand_name: "Volvo Buses",
          score: 90,
        },
        {
          brand_name: "Proterra",
          score: 65,
        },
        {
          brand_name: "Olectra",
          score: 82,
        },
      ],
    },
    {
      metric_name: "Technology Innovation Score",
      brands: [
        {
          brand_name: "BYD",
          score: 90,
        },
        {
          brand_name: "Proterra",
          score: 88,
        },
        {
          brand_name: "Volvo Buses",
          score: 85,
        },
        {
          brand_name: "Switch Mobility",
          score: 75,
        },
        {
          brand_name: "Yutong",
          score: 82,
        },
        {
          brand_name: "Tata Motors",
          score: 78,
        },
      ],
    },
    {
      metric_name: "Market Penetration Index",
      brands: [
        {
          brand_name: "BYD",
          score: 95,
        },
        {
          brand_name: "Yutong",
          score: 90,
        },
        {
          brand_name: "Volvo Buses",
          score: 80,
        },
        {
          brand_name: "Tata Motors",
          score: 70,
        },
        {
          brand_name: "Switch Mobility",
          score: 60,
        },
        {
          brand_name: "Olectra",
          score: 55,
        },
      ],
    },
    {
      metric_name: "Customer Satisfaction Index",
      brands: [
        {
          brand_name: "Volvo Buses",
          score: 88,
        },
        {
          brand_name: "BYD",
          score: 85,
        },
        {
          brand_name: "Yutong",
          score: 82,
        },
        {
          brand_name: "Tata Motors",
          score: 80,
        },
        {
          brand_name: "Switch Mobility",
          score: 72,
        },
        {
          brand_name: "Proterra",
          score: 70,
        },
      ],
    },
    {
      metric_name: "Total Cost of Ownership Competitiveness",
      brands: [
        {
          brand_name: "BYD",
          score: 90,
        },
        {
          brand_name: "Yutong",
          score: 88,
        },
        {
          brand_name: "Tata Motors",
          score: 85,
        },
        {
          brand_name: "Olectra",
          score: 82,
        },
        {
          brand_name: "Switch Mobility",
          score: 75,
        },
        {
          brand_name: "Volvo Buses",
          score: 70,
        },
      ],
    },
    {
      metric_name: "Government/Fleet Operator Trust Score",
      brands: [
        {
          brand_name: "BYD",
          score: 90,
        },
        {
          brand_name: "Tata Motors",
          score: 88,
        },
        {
          brand_name: "Yutong",
          score: 85,
        },
        {
          brand_name: "Volvo Buses",
          score: 83,
        },
        {
          brand_name: "Switch Mobility",
          score: 75,
        },
        {
          brand_name: "Olectra",
          score: 78,
        },
      ],
    },
  ],
  positioning: {
    competitive_advantages: [
      "Strong Indian market position with Ashok Leyland backing",
      "Dual manufacturing capability (India + UK)",
      "Growing order book (1,520 electric buses as of Sept 2024)",
      "Local manufacturing advantage in India",
    ],
    improvement_areas: [
      "Limited global presence vs. Chinese competitors",
      "Smaller scale and higher costs",
      "Weak social media presence and brand awareness",
      "Technology development behind leaders",
      "UK operations facing challenges",
    ],
    disruption_watchlist: ["PMI Electro Mobility Solutions", "Lion Electric Company", "Arrival", "Ebusco", "Wrightbus"],
  },
};

const audienceIntelligence: AudienceIntelligence = {
  audience_summary:
    "Switch Mobility operates in the B2B electric commercial vehicle market, selling electric buses and light commercial vehicles to government transit authorities, private fleet operators, and commercial businesses. The target audience consists of organizational buyers and decision-making committees rather than individual consumers.",
  core_demographics: {
    primary_age_range: "45-54",
    gender_split: {
      male: 72.5,
      female: 27.5,
    },
    income_median_usd: 80000,
    location_type: "Urban",
    demographics_distribution: {
      age: [
        { age_range: "25-34", percentage: 8 },
        { age_range: "35-44", percentage: 22 },
        { age_range: "45-54", percentage: 38 },
        { age_range: "55-64", percentage: 24 },
        { age_range: "65+", percentage: 8 },
      ],
      education: [
        { education_level: "Bachelor's+", percentage: 48 },
        { education_level: "Graduate Degree", percentage: 32 },
        { education_level: "Some College", percentage: 14 },
        { education_level: "High School", percentage: 6 },
      ],
      geographic: [
        { location: "Urban", percentage: 58 },
        { location: "Suburban", percentage: 28 },
        { location: "Rural", percentage: 14 },
      ],
    },
  },
  occupation_distribution: [
    { occupation: "Transit Authority", percentage: 35 },
    { occupation: "Private Fleet Operators", percentage: 25 },
    { occupation: "Procurement & Supply Chain", percentage: 18 },
    { occupation: "Engineering & Technical Decision Makers", percentage: 14 },
    { occupation: "Sustainability / ESG Leads", percentage: 8 },
  ],
  interests: {
    top_categories: [
      {
        category: "Sustainability & Environmental Impact",
        score: 9,
      },
      {
        category: "Fleet Technology & Telematics",
        score: 8,
      },
      {
        category: "Total Cost of Ownership (TCO) Analysis",
        score: 9,
      },
      {
        category: "Government Incentives & Funding",
        score: 8,
      },
      {
        category: "Charging Infrastructure",
        score: 8,
      },
      {
        category: "Regulatory Compliance & Policy",
        score: 8,
      },
      {
        category: "Operational Efficiency & Route Optimization",
        score: 7,
      },
      {
        category: "Driver Safety & Training",
        score: 7,
      },
      {
        category: "Industry Conferences & Professional Development",
        score: 6,
      },
      {
        category: "Supply Chain & Logistics Innovation",
        score: 6,
      },
    ],
    brand_affinities: [
      {
        brand: "BYD",
        affinity: 37.5,
      },
      {
        brand: "Volvo Buses",
        affinity: 32.5,
      },
      {
        brand: "Proterra",
        affinity: 27.5,
      },
      {
        brand: "Daimler/Mercedes-Benz",
        affinity: 27.5,
      },
      {
        brand: "Tata Motors",
        affinity: 32.5,
      },
      {
        brand: "Siemens",
        affinity: 22.5,
      },
      {
        brand: "Dana",
        affinity: 20,
      },
      {
        brand: "Geotab / Samsara",
        affinity: 25,
      },
      {
        brand: "ChargePoint / ABB",
        affinity: 22.5,
      },
      {
        brand: "APTA / NAFA",
        affinity: 17.5,
      },
    ],
  },
  behavioral_insights: {
    shopping_behavior: {
      purchase_channel_distribution: {
        "Government RFP/Tender": 70.7,
        "Direct B2B Sales": 17.1,
        "Leasing/Fleet Management Companies": 9.8,
        "Dealer Network": 2.4,
      },
      purchase_frequency: "0.15-0.25 purchases per year",
      avg_order_value_usd: 16500000,
    },
    decision_journey: {
      stages: [
        {
          stage: 1,
          name: "Fleet Electrification Planning",
          description: "Transit authority identifies need to replace aging diesel fleet, comply with emissions mandates, or meet sustainability goals",
          duration_days: 135,
        },
        {
          stage: 2,
          name: "Technology & Vendor Research",
          description:
            "B2B buyers are nearly 70% through purchasing process before engaging with sellers; anonymous research phase is decision-making stage. Buying committee researches electric bus technologies, charging infrastructure, TCO models, and vendor capabilities",
          duration_days: 180,
        },
        {
          stage: 3,
          name: "Specification & Procurement Planning",
          description:
            "Buying center defines technical specifications, desired features, quantity through collaboration among members. Develop RFP/tender documents, secure budget approval, identify funding sources",
          duration_days: 90,
        },
        {
          stage: 4,
          name: "Proposal Review & Pilot Testing",
          description: "Thorough review of proposals with critical eye to supplier capabilities, reputation, warranties, price. May include pilot deployments, site visits, reference checks",
          duration_days: 135,
        },
        {
          stage: 5,
          name: "Award & Deployment",
          description: "After selecting suppliers, B2B buyer negotiates order details. Finalize contracts, coordinate delivery schedules, plan charging infrastructure, arrange driver training",
          duration_days: 90,
        },
      ],
    },
    media_and_device_usage: {
      device_preference: {
        Desktop: 54.8,
        Mobile: 26.2,
        Tablet: 11.9,
        "In-person": 7.1,
      },
      media_channels: {
        "Industry Publications (digital)": 21.2,
        LinkedIn: 18,
        "Industry Conferences/Trade Shows": 14.9,
        "Direct Email/Newsletters": 12.2,
        "Webinars/Virtual Events": 9.9,
        "Google Search/SEO": 8.1,
        YouTube: 5.4,
        "Industry Associations": 4.5,
        "Peer Referrals/Word-of-Mouth": 3.6,
        "Twitter/X": 2.3,
      },
    },
  },
  platforms: {
    distribution: {
      "Industry Publications (digital)": 21,
      LinkedIn: 18,
      "Industry Conferences/Trade Shows": 15,
      "Direct Email/Newsletters": 12,
      "Webinars/Virtual Events": 10,
      "Google Search/SEO": 8,
      YouTube: 5,
      "Industry Associations": 4,
      "Peer Referrals/Word-of-Mouth": 4,
      "Twitter/X": 3,
    },
  },
};

const socialAndDigital: SocialAndDigital = {
  platform_metrics: [
    {
      platform: "LinkedIn",
      followers: 56282,
      health_score: 85,
    },
    {
      platform: "Instagram",
      followers: 41000,
      health_score: 75,
    },
    {
      platform: "Twitter/X",
      followers: 3793,
      health_score: 60,
    },
    {
      platform: "Facebook",
      followers: 0,
      health_score: 0,
    },
    {
      platform: "YouTube",
      followers: 0,
      health_score: 0,
    },
    {
      platform: "TikTok",
      followers: 0,
      health_score: 0,
    },
    {
      platform: "Pinterest",
      followers: 0,
      health_score: 0,
    },
  ],
  sentiment_distribution: [
    {
      label: "Positive",
      percentage: 10,
    },
    {
      label: "Neutral",
      percentage: 80,
    },
    {
      label: "Negative",
      percentage: 10,
    },
  ],
  recent_comments: [],
  engagement: {
    engagement_by_platform: [
      {
        platform: "LinkedIn",
        engagement_rate: 3,
      },
      {
        platform: "Instagram",
        engagement_rate: 1,
      },
      {
        platform: "Twitter/X",
        engagement_rate: 10,
      },
      {
        platform: "Facebook",
        engagement_rate: 10,
      },
      {
        platform: "YouTube",
        engagement_rate: 0,
      },
    ],
    best_performing_content_types: [
      {
        content_type: "Product Launch Videos",
        performance: 9,
      },
      {
        content_type: "Behind-the-Scenes (Manufacturing)",
        performance: 9,
      },
      {
        content_type: "Sustainability Stories",
        performance: 8,
      },
      {
        content_type: "Customer Testimonials/Case Studies",
        performance: 8,
      },
      {
        content_type: "Reels/Short-Form Video",
        performance: 9,
      },
      {
        content_type: "Carousel Posts (LinkedIn)",
        performance: 7,
      },
      {
        content_type: "Employee/Hiring Posts",
        performance: 7,
      },
    ],
    follower_growth_trend: [
      {
        month: "January 2025",
        followers: 10000,
      },
      {
        month: "February 2025",
        followers: 56282,
      },
      {
        month: "March 2025",
        followers: 36282,
      },
      {
        month: "April 2025",
        followers: 39020,
      },
      {
        month: "May 2025",
        followers: 45020,
      },
      {
        month: "June 2025",
        followers: 48020,
      },
      {
        month: "July 2025",
        followers: 50020,
      },
      {
        month: "August 2025",
        followers: 52020,
      },
      {
        month: "September 2025",
        followers: 54020,
      },
      {
        month: "October 2025",
        followers: 56020,
      },
      {
        month: "November 2025",
        followers: 58020,
      },
      {
        month: "December 2025",
        followers: 60020,
      },
    ],
  },
};

export const mBrandIntelligenceSwitchMobility: BrandIntelligence = {
  brand,
  creative_system: creativeSystem,
  market_intelligence: marketIntelligence,
  competitive_intel: competitiveIntel,
  audience_intelligence: audienceIntelligence,
  social_and_digital: socialAndDigital,
};
